import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    TextField,
    Autocomplete,
    IconButton,
    Portal,
    Popper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Draggable from 'react-draggable';
import { styled } from '@mui/system';
import EnumsService from '../../../Services/enums.service';
import { toast } from 'react-toastify';
import Constants from '../../../assests/Constants/constants';
import { styledInput } from '../../../Utils/styledInputHelper';
import '../../EditModal/EditModal.css';

// Format sort code group options for Autocomplete
const formatSortCodeGroupOptions = (sortCodesGroupOptions) => {
    return sortCodesGroupOptions?.map(option => ({
        label: option.sortCodeGroup || option.sortCodeGroupName,
        value: option.sortCodeGroup || option.sortCodeGroupName,
    })) || [];
};

const StyledPopper = styled(Popper)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 'var(--Spacing-md, 8px)',
        background: 'var(--white, #FFF)',
        boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
    },
    '& .MuiAutocomplete-option': {
        borderRadius: 'var(--Spacing-sm, 4px)',
        '&[aria-selected="true"], &.Mui-focused, &:hover': {
            background: 'var(--orange, #F9AA2A) !important',
        },
    },
}));

const StyledListItem = styled('li')({
    '&:hover': { backgroundColor: '#F5F5F6', cursor: 'pointer' },
});

const CreateDepreciationSortCodeDialog = ({
    open,
    onClose,
    client,
    token,
    direction = 'rtl',
    existingSortCodes = [],
    assetGroups = [],
    onSaveSuccess,
    initialSortCodeName = null // Optional: pre-fill the sort code name when dialog opens
}) => {
    const { t } = useTranslation();
    
    // Default values - get from Constants.AccountCardPalCode
    const defaultSortCodeGroup = useMemo(() => {
        const group = Constants.AccountCardPalCode?.find(
            item => item.value === 'הוצאות הנהלה וכלליות'
        );
        return group?.value || 'הוצאות הנהלה וכלליות'; // Fallback to hardcoded value if not found
    }, []);
    const defaultSortCodeVatType = 0; // NotParticipating (לא משתתף)
    const sortCodeIdPrefix = Constants.DepreciationPrefixes.SortCode;
    
    const [formData, setFormData] = useState({
        sortCodeId: '',
        sortCodeName: '',
        sortCodeGroup: defaultSortCodeGroup,
        sortCodeVatType: defaultSortCodeVatType,
        vat: 0, // Default VAT to 0
    });
    
    const [selectedAssetGroup, setSelectedAssetGroup] = useState(null);
    const [sortCodesGroupOptions, setSortCodesGroupOptions] = useState([]);
    const [container] = useState(() => document.createElement('div'));
    const nodeRef = useRef(null);

    // Fetch sort code groups when dialog opens
    useEffect(() => {
        if (open && client && client.accountantId && token) {
            const fetchSortCodeGroups = async () => {
                try {
                    const response = await EnumsService.GetSortCodesGroupByAccountantId(client.accountantId, token);
                    if (response.ok) {
                        const groupsData = await response.json();
                        setSortCodesGroupOptions(groupsData);
                    }
                } catch (error) {
                    console.error('Error fetching sort code groups:', error);
                }
            };
            fetchSortCodeGroups();
        }
    }, [open, client, token]);

    // Initialize form data and auto-increment sortCodeId when dialog opens
    useEffect(() => {
        if (open) {
            // Auto-increment sortCodeId
            let newSortCodeId = '1';
            if (existingSortCodes && existingSortCodes.length > 0) {
                const lastCodeId = Math.max(
                    ...existingSortCodes.map((item) => Number(item.sortCodeId) || 0)
                );
                newSortCodeId = String(lastCodeId + 1);
            }
            
            // Use initialSortCodeName if provided, otherwise use prefix
            const initialName = initialSortCodeName || sortCodeIdPrefix;
            
            setFormData({
                sortCodeId: newSortCodeId,
                sortCodeName: initialName,
                sortCodeGroup: defaultSortCodeGroup,
                sortCodeVatType: defaultSortCodeVatType,
                vat: 0, // Default VAT to 0
            });
            
            // If initialSortCodeName is provided, try to find matching asset group
            if (initialSortCodeName && assetGroups.length > 0) {
                const groupName = initialSortCodeName.replace(sortCodeIdPrefix, '').trim();
                const matchingGroup = assetGroups.find(g => g.assetGroupName === groupName);
                setSelectedAssetGroup(matchingGroup || null);
            } else {
                setSelectedAssetGroup(null);
            }
        }
    }, [open, existingSortCodes, initialSortCodeName, assetGroups, sortCodeIdPrefix]);

    // Container management for Portal
    useEffect(() => {
        if (open) {
            document.body.appendChild(container);
            const updateModalPosition = () => {
                const modalWidth = 700;
                const modalHeight = nodeRef.current ? nodeRef.current.offsetHeight : 0;
                const screenWidth = window.innerWidth;
                const screenHeight = window.innerHeight;
                const x = (screenWidth - modalWidth) / 2;
                const y = (screenHeight - modalHeight) / 2;
                if (nodeRef.current) {
                    nodeRef.current.style.left = `${x}px`;
                    nodeRef.current.style.top = `${y}px`;
                }
            };
            setTimeout(updateModalPosition, 0);
            window.addEventListener('resize', updateModalPosition);
            return () => {
                try {
                    document.body.removeChild(container);
                } catch (e) {
                    // Ignore
                }
                window.removeEventListener('resize', updateModalPosition);
            };
        } else {
            try {
                document.body.removeChild(container);
            } catch (e) {
                // Ignore
            }
        }
    }, [open, container]);

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Helper function for error styling
    const inputPropsWithError = (hasError) => ({
        ...styledInput(direction, hasError),
    });

    // Check if sortCodeName is valid (has content after the prefix)
    const isSortCodeNameValid = () => {
        if (!formData.sortCodeName) return false;
        const trimmedName = formData.sortCodeName.trim();
        // Check if it's only the prefix or empty
        return trimmedName !== sortCodeIdPrefix && trimmedName.length > sortCodeIdPrefix.length;
    };

    // Validation errors - similar to AddAccountCardModal.jsx
    const fieldErrors = useMemo(
        () => ({
            sortCodeName: !isSortCodeNameValid(),
        }),
        [formData.sortCodeName]
    );

    const handleSave = async () => {
        // Validate required fields
        if (!formData.sortCodeId || !formData.sortCodeName || formData.vat === null || formData.vat === undefined) {
            toast.error(t('PleaseFillAllRequiredFields') || 'Please fill all required fields');
            return;
        }

        // Validate that sortCodeName has content after the prefix
        if (!isSortCodeNameValid()) {
            toast.error(t('PleaseFillSortCodeName') || 'Please enter a valid Sort Code Name (must have content after the prefix)');
            return;
        }

        // Check for duplicate sortCodeId
        const isDuplicateId = existingSortCodes.some(
            (item) => String(item.sortCodeId).trim() === String(formData.sortCodeId).trim()
        );
        if (isDuplicateId) {
            toast.error(t('SortCodeIdAlreadyExists') || 'Sort Code ID already exists');
            return;
        }

        // Check for duplicate sortCodeName
        const isDuplicateName = existingSortCodes.some(
            (item) => item.sortCodeName?.trim().toLowerCase() === formData.sortCodeName?.trim().toLowerCase()
        );
        if (isDuplicateName) {
            toast.error(t('SortCodeNameAlreadyExists') || 'Sort Code Name already exists');
            return;
        }

        try {
            const response = await EnumsService.AddSortCode(
                client.accountantId,
                formData.sortCodeName,
                formData.sortCodeId,
                formData.sortCodeGroup,
                null, // creditAccount
                null, // debitAccount
                formData.sortCodeVatType,
                formData.vat, // vat from form data
                true, // participatesInProfitAndLoss - must be true to use the actual sortCodeGroup instead of "הוצאות כלליות"ת
                null, // pcnTransactionType
                null, // code6111Id
                0, // inventoryType
                token
            );

            if (response.ok) {
                toast.success(t('SortCodeCreatedSuccessfully') || 'Sort Code created successfully');
                if (onSaveSuccess) {
                    // Pass the newly created sort code ID to the callback
                    await onSaveSuccess(formData.sortCodeId);
                }
                onClose();
            } else {
                const errorText = await response.text();
                toast.error(t('ErrorCreatingSortCode') || `Error: ${errorText}`);
            }
        } catch (error) {
            console.error('Error creating sort code:', error);
            toast.error(t('ErrorCreatingSortCode') || 'Error creating sort code');
        }
    };

    // Input styling (for fields without error validation)
    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '40px',
            padding: '0 8px',
            borderRadius: '6px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'ltr' ? 'left' : 'right',
        },
    };

    if (!open) return null;

    return (
        <Portal container={container}>
            <Draggable handle=".handle" nodeRef={nodeRef}>
                <div
                    ref={nodeRef}
                    style={{
                        position: 'absolute',
                        top: '10%',
                        left: '20%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '10px',
                        minHeight: '28vh',
                        overflowY: 'auto',
                        minWidth: '600px',
                        zIndex: 1300,
                        display: 'flex',
                        flexDirection: 'row',
                        direction: direction,
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
                        border: '1px solid #304fff',
                    }}
                >
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div
                            className="handle"
                            style={{
                                cursor: 'move',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                marginBottom: '6px',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <IconButton
                                onClick={onClose}
                                size="small"
                                style={{
                                    color: '#666',
                                    cursor: 'pointer',
                                }}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>

                            <div className="edit-title">
                                {t('NewSortCode')}
                            </div>

                            <div style={{ width: '40px' }}></div>
                        </div>

                        <div className={`form-container ${direction}`} style={{ gap: '8px' }}>
                            {/* Row 1: SortCode Id */}
                            <div className="form-row">
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('SortCode Id')}
                                        <span className="required-asterisk">*</span>
                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('SortCode Id')}
                                        value={formData.sortCodeId || ''}
                                        onChange={(e) => handleFormChange('sortCodeId', e.target.value)}
                                        variant="standard"
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        required
                                    />
                                </div>

                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('SortCode Name')}
                                        <span className="required-asterisk">*</span>
                                    </div>
                                    {assetGroups.length > 0 ? (
                                        <Autocomplete
                                            freeSolo
                                            options={assetGroups}
                                            getOptionLabel={(option) => {
                                                if (typeof option === 'string') return option;
                                                return option.assetGroupName || '';
                                            }}
                                            value={formData.sortCodeName}
                                            inputValue={formData.sortCodeName}
                                            onChange={(_, newValue) => {
                                                if (typeof newValue === 'string') {
                                                    handleFormChange('sortCodeName', newValue);
                                                    const matchingGroup = assetGroups.find(g => 
                                                        newValue.includes(g.assetGroupName)
                                                    );
                                                    setSelectedAssetGroup(matchingGroup || null);
                                                } else if (newValue && newValue.assetGroupName) {
                                                    const fullName = `${sortCodeIdPrefix}${newValue.assetGroupName}`;
                                                    handleFormChange('sortCodeName', fullName);
                                                    setSelectedAssetGroup(newValue);
                                                } else {
                                                    handleFormChange('sortCodeName', sortCodeIdPrefix);
                                                    setSelectedAssetGroup(null);
                                                }
                                            }}
                                            onInputChange={(_, newInputValue, reason) => {
                                                if (reason === 'input' || reason === 'clear') {
                                                    handleFormChange('sortCodeName', newInputValue);
                                                    const matchingGroup = assetGroups.find(g => 
                                                        newInputValue.includes(g.assetGroupName)
                                                    );
                                                    setSelectedAssetGroup(matchingGroup || null);
                                                }
                                            }}
                                            filterOptions={(options, params) => {
                                                const searchValue = params.inputValue.toLowerCase();
                                                const prefixLower = sortCodeIdPrefix.toLowerCase();
                                                
                                                const filtered = options.filter(option => {
                                                    const groupName = option.assetGroupName?.toLowerCase() || '';
                                                    const searchWithoutPrefix = searchValue.replace(prefixLower, '').trim();
                                                    return groupName.includes(searchWithoutPrefix) || 
                                                           groupName.includes(searchValue) ||
                                                           `${prefixLower}${groupName}`.includes(searchValue);
                                                });
                                                
                                                return filtered;
                                            }}
                                            renderOption={(props, option) => {
                                                const displayText = typeof option === 'string' 
                                                    ? option 
                                                    : `${sortCodeIdPrefix}${option.assetGroupName || ''}`;
                                                return (
                                                    <li {...props} key={option.assetGroupId || option}>
                                                        {displayText}
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => {
                                                const errorProps = inputPropsWithError(fieldErrors.sortCodeName);
                                                return (
                                                    <TextField
                                                        {...params}
                                                        variant="standard"
                                                        placeholder={t('TypeOrSelectAssetGroup') || `${sortCodeIdPrefix}Type or select asset group`}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            ...errorProps,
                                                            sx: {
                                                                ...params.InputProps?.sx,
                                                                ...errorProps.sx,
                                                            },
                                                        }}
                                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                                        required
                                                    />
                                                );
                                            }}
                                            PopperComponent={(props) => (
                                                <StyledPopper {...props} style={{ ...props.style, direction }} />
                                            )}
                                        />
                                    ) : (
                                        <TextField
                                            fullWidth
                                            placeholder={t('SortCode Name')}
                                            value={formData.sortCodeName || ''}
                                            onChange={(e) => handleFormChange('sortCodeName', e.target.value)}
                                            variant="standard"
                                            InputProps={inputPropsStyle}
                                            inputProps={inputStyle}
                                            required
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Row 3: SortCode Group and VAT */}
                            <div className="form-row">
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('SelectSortCodeGroupName')}
                                    </div>
                                    <Autocomplete
                                        options={formatSortCodeGroupOptions(sortCodesGroupOptions)}
                                        getOptionLabel={(option) => option.label || option.value || ''}
                                        value={
                                            formatSortCodeGroupOptions(sortCodesGroupOptions).find(
                                                (option) => option.value === formData.sortCodeGroup
                                            ) || (formData.sortCodeGroup ? { label: formData.sortCodeGroup, value: formData.sortCodeGroup } : null)
                                        }
                                        onChange={(event, newValue) => {
                                            handleFormChange('sortCodeGroup', newValue ? newValue.value : defaultSortCodeGroup);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('SelectSortCodeGroupName')}
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    ...inputPropsStyle,
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    ...inputStyle,
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <StyledListItem {...props}>
                                                {option.label || option.value}
                                            </StyledListItem>
                                        )}
                                        PopperComponent={(props) => (
                                            <StyledPopper {...props} style={{ ...props.style, direction }} />
                                        )}
                                    />
                                </div>

                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('VAT')}
                                        <span className="required-asterisk">*</span>
                                    </div>
                                    <Autocomplete
                                        options={Constants.VATPercentage.map(v => ({
                                            label: `${v}%`,
                                            value: v
                                        }))}
                                        getOptionLabel={(option) => option.label || `${option.value}%`}
                                        value={
                                            Constants.VATPercentage
                                                .map(v => ({ label: `${v}%`, value: v }))
                                                .find(option => option.value === formData.vat) || null
                                        }
                                        onChange={(event, newValue) => {
                                            handleFormChange('vat', newValue ? newValue.value : 0);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('VAT')}
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    ...inputPropsStyle,
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    ...inputStyle,
                                                }}
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <StyledListItem {...props}>
                                                {option.label || `${option.value}%`}
                                            </StyledListItem>
                                        )}
                                        PopperComponent={(props) => (
                                            <StyledPopper {...props} style={{ ...props.style, direction }} />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Buttons */}
                            <div className="button-container" style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                                <div onClick={onClose} className="edit-cancel-button">
                                    {t('Cancel')}
                                </div>
                                <div
                                    onClick={handleSave}
                                    className="edit-save-button"
                                    style={{ 
                                        opacity: (!formData.sortCodeId || !formData.sortCodeName || fieldErrors.sortCodeName || formData.vat === null || formData.vat === undefined) ? 0.5 : 1, 
                                        pointerEvents: (!formData.sortCodeId || !formData.sortCodeName || fieldErrors.sortCodeName || formData.vat === null || formData.vat === undefined) ? 'none' : 'auto' 
                                    }}
                                >
                                    {t('Save')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Draggable>
        </Portal>
    );
};

export default CreateDepreciationSortCodeDialog;

